/**
 * Brand motion primitives — Inspired Edge "feel" layer.
 *
 * Restrained, fast, accessible entrance motion with **no runtime dependency**:
 * these are thin React wrappers over the `.animate-rise` / `.animate-fade`
 * keyframes defined in `src/index.css` (and `tailwind.config.ts`). framer-motion
 * is deliberately NOT a Relay dependency — keeping motion in CSS means zero
 * bundle cost and nothing over ~250ms.
 *
 * Reduced-motion is honored twice over: the CSS disables the animations under
 * `prefers-reduced-motion: reduce`, and `useReducedMotion()` additionally drops
 * the animation class (and any stagger delay) so reduced-motion users see
 * content at its final state with no opacity/transform flash.
 *
 * Usage:
 *   <FadeIn>…</FadeIn>                      // single element fade + 12px rise
 *   <FadeIn variant="fade" delay={80}>…</FadeIn>
 *   <Stagger step={40}>                     // staggered row of cards
 *     <StaggerItem>…</StaggerItem>
 *     <StaggerItem>…</StaggerItem>
 *   </Stagger>
 *   <RouteTransition routeKey={location.pathname}>…</RouteTransition>
 */
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
  type ElementType,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Subscribe to the OS "reduce motion" preference. SSR/no-`matchMedia` safe
 * (Relay is a client SPA, but we guard anyway). Reusable beyond these wrappers
 * — e.g. to skip the Wave 1 stat count-up.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(REDUCED_MOTION_QUERY).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    // Safari < 14 only supports the deprecated add/removeListener API.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return reduced;
}

type MotionVariant = "rise" | "fade";

function animationClass(variant: MotionVariant): string {
  return variant === "fade" ? "animate-fade" : "animate-rise";
}

interface FadeInProps extends HTMLAttributes<HTMLElement> {
  /** Element/component to render. Defaults to `div`. */
  as?: ElementType;
  /** `rise` = fade + 12px lift (default); `fade` = opacity only. */
  variant?: MotionVariant;
  /** Delay before the entrance begins, in ms. */
  delay?: number;
  children?: ReactNode;
}

/** Fade (optionally rising) a single element in on first paint. */
export function FadeIn({
  as: Tag = "div",
  variant = "rise",
  delay = 0,
  className,
  style,
  children,
  ...rest
}: FadeInProps) {
  const reduced = useReducedMotion();
  return (
    <Tag
      className={cn(!reduced && animationClass(variant), className)}
      style={reduced ? style : { animationDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

interface StaggerItemProps extends HTMLAttributes<HTMLElement> {
  /** Element/component to render. Defaults to `div`. */
  as?: ElementType;
  /** `rise` = fade + 12px lift (default); `fade` = opacity only. */
  variant?: MotionVariant;
  children?: ReactNode;
  /** Injected by `<Stagger>`. Do not set manually. */
  __staggerDelay?: number;
}

/** A single item inside a `<Stagger>`; receives its delay from the parent. */
export function StaggerItem({
  as: Tag = "div",
  variant = "rise",
  className,
  style,
  children,
  __staggerDelay = 0,
  ...rest
}: StaggerItemProps) {
  const reduced = useReducedMotion();
  return (
    <Tag
      className={cn(!reduced && animationClass(variant), className)}
      style={reduced ? style : { animationDelay: `${__staggerDelay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

interface StaggerProps extends HTMLAttributes<HTMLElement> {
  /** Element/component to render. Defaults to `div`. */
  as?: ElementType;
  /** Per-item delay step in ms (default 40 — one frame-ish apart). */
  step?: number;
  /** Delay before the first item animates, in ms. */
  baseDelay?: number;
  children?: ReactNode;
}

/**
 * Reveal direct `<StaggerItem>` children in sequence. Non-item children (e.g. a
 * divider) pass through untouched and don't consume a stagger slot.
 */
export function Stagger({
  as: Tag = "div",
  step = 40,
  baseDelay = 0,
  className,
  children,
  ...rest
}: StaggerProps) {
  const reduced = useReducedMotion();
  let index = 0;
  const items = Children.map(children, (child) => {
    if (!isValidElement(child) || child.type !== StaggerItem) {
      return child;
    }
    const delay = reduced ? 0 : baseDelay + index * step;
    index += 1;
    return cloneElement(child as ReactElement<StaggerItemProps>, {
      __staggerDelay: delay,
    });
  });

  return (
    <Tag className={className} {...rest}>
      {items}
    </Tag>
  );
}

interface RouteTransitionProps extends HTMLAttributes<HTMLElement> {
  /** Element/component to render. Defaults to `div`. */
  as?: ElementType;
  /**
   * Remount key for the transition — pass `location.pathname` (or another
   * value that changes per navigation). `FadeIn`/`Stagger` only animate on
   * mount, so a route-content wrapper needs a key that changes with the
   * route to retrigger the entrance each time, the same way `AppShell`
   * already keys its `ErrorBoundary` by `location.pathname`.
   */
  routeKey: string;
  children?: ReactNode;
}

/**
 * Fades the routed page content in on navigation. Thin wrapper over
 * `FadeIn` — the `key` is what makes it a *route* transition rather than a
 * plain mount animation.
 */
export function RouteTransition({ routeKey, ...rest }: RouteTransitionProps) {
  return <FadeIn key={routeKey} variant="fade" {...rest} />;
}
